import React, { FC, memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Dropdown } from 'tea-component';
import BasicImage from '@home/components/BasicImage';
import CommonBubble from '@home/components/CommonBubble';
import SkillInstallAgentIcon from '@/pages/aigc/assets/svg/skill-install-agent.svg';
import SkillInstallDownloadIcon from '@/pages/aigc/assets/svg/skill-install-download.svg';
import {
  ESkillSecurityStatus,
  type ISkillData,
} from '../../types';
import './index.scss';

const PREFIX = 'dba-skill-install-dropdown';

/**
 * Skill 安装/下载下拉菜单项定义
 */
export interface ISkillInstallMenuItem {
  /** 菜单项唯一标识 */
  key: string;
  /** 中文文案 */
  label_cn: string;
  /** 英文文案 */
  label_en: string;
  /** 菜单项图标地址 */
  icon?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 点击回调 */
  onClick?: (skill: ISkillData) => void;
}

/**
 * Skill 安装/下载下拉组件 Props（与 SkillCard 操作区复用）
 */
export interface ISkillInstallDropdownProps {
  /** 目标 Skill 数据 */
  skill: ISkillData;
  /** 是否英文环境 */
  isEN: boolean;
  /** 是否正在下载 */
  isDownloading?: boolean;
  /** 安装 Skill 回调 */
  onInstall?: (skillId: string) => void;
  /** 下载 Skill 包回调 */
  onDownload?: (skill: ISkillData) => void;
  /** 自定义菜单项（不传则使用默认安装/下载菜单） */
  dropdownMenuItems?: ISkillInstallMenuItem[];
  /** 外层 className */
  className?: string;
}

/**
 * Skill 安装/下载下拉（与 SkillCard 操作区复用）
 */
const SkillInstallDropdown: FC<ISkillInstallDropdownProps> = ({
  skill,
  isEN,
  isDownloading = false,
  onInstall,
  onDownload,
  dropdownMenuItems,
  className,
}) => {
  const { formatMessage } = useIntl();
  const isInstalled = skill.is_installed;
  const isSafe = skill.security_status === ESkillSecurityStatus.SAFE;

  const menuItems = useMemo<ISkillInstallMenuItem[]>(() => {
    if (dropdownMenuItems) {
      return dropdownMenuItems;
    }

    const isDownloadDisabled = skill.downloadable === 0 || isDownloading;
    const getDownloadLabel = (en: boolean) => {
      if (skill.downloadable === 0) {
        return en ? 'Download not available' : '暂不开放下载';
      }
      if (isDownloading) {
        return en ? 'Downloading...' : '正在下载...';
      }
      return en ? 'Download to Local' : '下载到本地';
    };

    return [
      {
        key: 'install',
        label_cn: isInstalled ? '已安装到 DataBrain' : '安装到 DataBrain',
        label_en: isInstalled ? 'Installed to DataBrain' : 'Install to DataBrain',
        icon: SkillInstallAgentIcon,
        disabled: isInstalled,
        onClick: () => {
          if (isSafe && !isInstalled) {
            onInstall?.(skill.skill_id);
          }
        },
      },
      {
        key: 'download',
        label_cn: getDownloadLabel(false),
        label_en: getDownloadLabel(true),
        icon: SkillInstallDownloadIcon,
        disabled: isDownloadDisabled,
        onClick: () => {
          if (isSafe && !isDownloadDisabled) {
            onDownload?.(skill);
          }
        },
      },
    ];
  }, [
    dropdownMenuItems,
    isDownloading,
    isInstalled,
    isSafe,
    onDownload,
    onInstall,
    skill,
  ]);

  const renderDropdownContent = useCallback(
    (close: () => void) => (
      <div className={`${PREFIX}__dropdown`}>
        {menuItems.map((item) => (
          <div
            key={item.key}
            className={classNames(`${PREFIX}__dropdown-item`, {
              [`${PREFIX}__dropdown-item--disabled`]: item.disabled,
            })}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) {
                close();
                return;
              }
              item.onClick?.(skill);
              close();
            }}
          >
            {item.icon && (
              <BasicImage
                src={item.icon}
                alt=""
                className={`${PREFIX}__dropdown-icon`}
              />
            )}
            <span>{isEN ? item.label_en : item.label_cn}</span>
          </div>
        ))}
      </div>
    ),
    [isEN, menuItems, skill],
  );

  const triggerBtn = (
    <div className={`${PREFIX}__trigger`}>
      <BasicImage
        src={SkillInstallDownloadIcon}
        alt=""
        className={`${PREFIX}__trigger-icon`}
      />
    </div>
  );

  return (
    <div className={classNames(PREFIX, className)} onClick={(e) => e.stopPropagation()}>
      <CommonBubble
        placement="top"
        content={formatMessage({ id: 'dba_skills_card_install_tooltip' })}
      >
        <Dropdown
          clickClose={false}
          button={triggerBtn}
          appearance="pure"
          boxClassName={`${PREFIX}__dropdown-box`}
        >
          {(close) => renderDropdownContent(close)}
        </Dropdown>
      </CommonBubble>
    </div>
  );
};

export default memo(SkillInstallDropdown);
